import { HttpCode } from "@myvp/shared/src/types"
import { GraphQLErrorExtensions } from "graphql"

export interface GraphQLRequestError {
  response: {
    errors: {
      message?: string
      extensions: {
        [key: string]: HttpCode | string | string[] | undefined
        errorCode: HttpCode | string
        errorKey?: string
        errorKeys?: string[]
        fields?: string | string[]
      }
    }[]
  }
}

type ErrorInput = {
  status?: string | number
  message?: string
} & { response?: GraphQLRequestError["response"] }

const checkCodegenNeeded = (error: ErrorInput) => {
  if (
    process.env.NODE_ENV === "development" &&
    error?.message?.includes?.(
      `Cannot read properties of undefined (reading 'filter')`
    )
  ) {
    // oxlint-disable-next-line no-alert
    window.alert("You need to run pnpm codegen")
  }
}

const parseError = (error: ErrorInput) => {
  // graphql-request returns an object that needs to be stringified then parsed again, if this fails, then it's using fetch
  return JSON.parse(JSON.stringify(error))
}

export const getErrorCode = (error: ErrorInput) => {
  let code
  try {
    checkCodegenNeeded(error)
    const newError = parseError(error)
    code = newError?.response?.errors?.[0]?.extensions?.errorCode
  } catch {}
  if (!code) {
    code = error?.status
  }
  return code ? String(code) : code
}

export const getErrorKey = (error: ErrorInput) => {
  let key
  try {
    checkCodegenNeeded(error)
    const newError = parseError(error)
    key = newError?.response?.errors?.[0]?.extensions?.errorKey
  } catch {}

  return key
}

export const getErrorFields = (error: ErrorInput) => {
  let fields: string[] = []
  try {
    checkCodegenNeeded(error)
    const newError = parseError(error)
    const parsedFields =
      newError?.response?.errors?.[0]?.extensions?.fields ?? []
    if (typeof parsedFields === "string") {
      fields = [parsedFields]
    } else {
      fields = parsedFields
    }
  } catch {}
  return fields
}

const errorKeyPropertyPattern = /^([A-Z][0-9]{3,4})\.(\S+)$/

export type ErrorKeysMap<T = string[]> = Map<string, Record<string, T> | null>

export function getErrorKeysMap(error: unknown): ErrorKeysMap | undefined
export function getErrorKeysMap<T>(
  error: unknown,
  mapValues: (value: string[]) => T
): ErrorKeysMap<T> | undefined
export function getErrorKeysMap<T>(
  error: unknown,
  mapValues?: (value: string[]) => T
): ErrorKeysMap<T | string[]> | undefined {
  let extensions: GraphQLErrorExtensions | undefined = undefined
  try {
    // graphql-request returns an object that needs to be stringified then parsed again.
    extensions = JSON.parse(JSON.stringify(error))?.response?.errors?.[0]
      ?.extensions
  } catch {
    return undefined
  }
  if (
    !extensions ||
    typeof extensions !== "object" ||
    !("errorKeys" in extensions) ||
    !Array.isArray(extensions.errorKeys) ||
    !extensions.errorKeys.every((item) => typeof item === "string") ||
    extensions.errorKeys.length <= 0
  ) {
    return undefined
  }

  const errorKeyMap = new Map<string, Record<string, T | string[]> | null>(
    extensions.errorKeys.map((errorKey) => [errorKey, null])
  )
  for (const [maybeKey, maybeValue] of Object.entries(extensions)) {
    const match = errorKeyPropertyPattern.exec(maybeKey)
    if (!match) continue
    const [, errorKey, argKey] = match

    let errorArgs = errorKeyMap.get(errorKey)
    // this `errorKey` wasn't present in the `extensions.errorKeys` array
    if (errorArgs === undefined) continue

    let arrayValue: string[]
    if (typeof maybeValue === "string") {
      arrayValue = [maybeValue]
    } else if (
      Array.isArray(maybeValue) &&
      maybeValue.every((item) => typeof item === "string")
    ) {
      arrayValue = maybeValue
    } else {
      continue
    }

    if (errorArgs === null) {
      // this is the first time we have a valid value for this `errorKey`
      // we create the record as late as possible to avoid having empty records as values.
      errorArgs = {}
      errorKeyMap.set(errorKey, errorArgs)
    }
    errorArgs[argKey] = mapValues ? mapValues(arrayValue) : arrayValue
  }
  return errorKeyMap
}

export function getErrorKeysList(
  error: unknown
): [string, Record<string, string[]> | null][] | undefined
export function getErrorKeysList<T>(
  error: unknown,
  mapValue: (value: string[]) => T
): [string, Record<string, T> | null][] | undefined
export function getErrorKeysList<T>(
  error: unknown,
  mapValue?: (value: string[]) => T
): [string, Record<string, T | string[]> | null][] | undefined {
  const errorKeysMap = mapValue
    ? getErrorKeysMap<T>(error, mapValue)
    : getErrorKeysMap(error)
  return errorKeysMap ? [...errorKeysMap] : undefined
}
